import { observable, action, computed } from 'mobx'
import {
  ILocationWithMetaDataContract,
  IUpdateLocationWithMetaDataContract,
  API,
  ICompanyWithMetaDataContract,
  IUpdateCompanyWithMetaDataContract,
  IBirchmountLocationContract,
  BirchmountStatus,
  BirchmountAPI,
  MetaDataType,
  MetaKeys,
} from '@getgreenline/homi-shared'

import { GrRegex } from '../../../../../utilities/helpers'

export class LocationMetaDataStore {
  @observable
  private initialBirchmountLocationId: number | null

  locationId: number

  locationName: string

  @observable
  birchmountLocationId: number | null

  @observable
  isLocationIdEditable = false

  @observable
  isUpdating = false

  constructor(locationWithMetaData: ILocationWithMetaDataContract<MetaDataType.INTEGER>) {
    this.locationId = locationWithMetaData.id

    this.locationName = locationWithMetaData.name

    let initialBirchmountLocationId = locationWithMetaData.metaData[MetaKeys.birchmountLocationId]

    if (initialBirchmountLocationId === undefined) {
      initialBirchmountLocationId = null
    }

    this.initialBirchmountLocationId = initialBirchmountLocationId

    this.birchmountLocationId = initialBirchmountLocationId
  }

  @action
  setBirchmountLocationId = (birchmountLocationId: number | null) => {
    this.birchmountLocationId = birchmountLocationId
  }

  @action
  revertBirchmountLocationId = () => {
    this.birchmountLocationId = this.initialBirchmountLocationId
  }

  @action
  setIsLocationIdEditable = (isLocationIdEditable: boolean) => {
    this.isLocationIdEditable = isLocationIdEditable
  }

  @action
  setIsUpdating = (isUpdating: boolean) => {
    this.isUpdating = isUpdating
  }

  @action
  saveLocationMetaData = async (companyId: number) => {
    const locationContract: IUpdateLocationWithMetaDataContract<MetaDataType> = {
      id: this.locationId,
      metaData: {
        [MetaKeys.birchmountLocationId]: this.birchmountLocationId,
      },
    }

    const updatedLocation: ILocationWithMetaDataContract<MetaDataType.INTEGER> =
      await API.updateLocation(companyId, this.locationId, locationContract, true)

    let initialBirchmountLocationId = updatedLocation.metaData[MetaKeys.birchmountLocationId]

    if (initialBirchmountLocationId === undefined) {
      initialBirchmountLocationId = null
    }

    this.setInitialBirchmountLocationId(initialBirchmountLocationId)
  }

  @action
  private setInitialBirchmountLocationId = (birchmountLocationId: number | null) => {
    this.initialBirchmountLocationId = birchmountLocationId
  }
}

export class BMLocationStore {
  @observable
  locationContract?: IBirchmountLocationContract

  @observable
  isLocationContractLoading = false

  @action
  getLocationContract = async (apiKey: string | null) => {
    const response = await BirchmountAPI.getLocationIds({ apiKey: apiKey || '' }, undefined)
    return this.setLocationContract(response)
  }

  @action
  setLocationContract = (locationContract?: IBirchmountLocationContract) => {
    this.locationContract = locationContract

    return this.locationContract
  }

  @action
  setIsLocationContractLoading = (isLocationContractLoading: boolean) => {
    this.isLocationContractLoading = isLocationContractLoading
  }
}

export class BMConfigStore extends BMLocationStore {
  private initialApiKey: string | null

  @observable
  apiKey: string | null

  @observable
  locationsWithMetaData: LocationMetaDataStore[]

  @observable
  isApiKeyEditable = false

  @observable
  isApiKeyUpdating = false

  @observable
  companyWithMetaData: ICompanyWithMetaDataContract<MetaDataType.STRING>

  constructor({
    companyWithMetaData,
    locationsWithMetaData,
  }: {
    companyWithMetaData: ICompanyWithMetaDataContract<MetaDataType.STRING>
    locationsWithMetaData: Array<ILocationWithMetaDataContract<MetaDataType.INTEGER>>
  }) {
    super()

    this.initialApiKey = companyWithMetaData.metaData[MetaKeys.birchmountApiKey] || null

    this.apiKey = companyWithMetaData.metaData[MetaKeys.birchmountApiKey] || null

    this.locationsWithMetaData = locationsWithMetaData.map(
      (locationWithMetaData) => new LocationMetaDataStore(locationWithMetaData),
    )

    this.companyWithMetaData = companyWithMetaData
  }

  @action
  setIsApiKeyEditable = (isApiKeyEditable: boolean) => {
    this.isApiKeyEditable = isApiKeyEditable
  }

  @action
  setApiKey = (apiKey: string | null) => {
    this.apiKey = apiKey === null ? null : this.sanitizeApiKey(apiKey)

    return this.apiKey
  }

  @action
  revertApiKey = () => {
    this.apiKey = this.initialApiKey
  }

  @action
  setIsApiKeyUpdating = (isUpdating: boolean) => {
    this.isApiKeyUpdating = isUpdating
  }

  @action
  saveCompanyMetaData = async (companyId: number) => {
    const companyContract: IUpdateCompanyWithMetaDataContract<MetaDataType> = {
      metaData: {
        [MetaKeys.birchmountApiKey]: this.apiKey,
      },
    }

    const updatedCompany: ICompanyWithMetaDataContract<MetaDataType.STRING> =
      await API.updateCompanyWithMetaData(companyId, companyContract, true)

    const initialApikey = updatedCompany.metaData[MetaKeys.birchmountApiKey] || null

    this.setInitialApiKey(initialApikey)
  }

  @action
  unsyncAllBirchmountIds = async (companyId: number) => {
    for (const locationWithMetaData of this.locationsWithMetaData) {
      if (locationWithMetaData.birchmountLocationId !== null) {
        locationWithMetaData.setBirchmountLocationId(null)
        await locationWithMetaData.saveLocationMetaData(companyId)
      }
    }
  }

  @action
  private setInitialApiKey = (apiKey: string | null) => {
    this.initialApiKey = apiKey
  }

  private sanitizeApiKey = (apiKey: string) => {
    return apiKey.trim().replace(GrRegex.unicodeRegex, '')
  }

  @computed
  get canSaveApiKey() {
    // Allow `null` to be saved
    if (!this.apiKey) {
      return true
    }

    return this.locationContract?.STATUS === BirchmountStatus.SUCCESS
  }
}
